import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Header, Item, Panel } from '.'
import { PanelProps } from './panel'
import { HeaderProps } from './header'
import { ItemProps } from './item'
import {
  CustomSlidingTutorialProps,
  SlidingTutorialContent
} from './sliding-tutorial-content'
import appLibsCVEIcon from '../../../images/tutorial/appLibsCVEIcon.png'
import appLibsLibraryIcon from '../../../images/tutorial/appLibsLibraryIcon.png'
import appLibsStatsIcon from '../../../images/tutorial/appLibsStatsIcon.png'
import { Button } from '@contrast-security-inc/design-system-buttons'

const itemsConfig = (intl): ItemProps[] => [
  {
    title: {
      id: 'title_librariesSlidingTutorial_explore',
      defaultMessage: 'Explore Libraries'
    },
    description: {
      id: 'detail_librariesSlidingTutorial_explore',
      defaultMessage:
        'Contrast will tell you what libraries are being used by your application, and whether they’re outdated or vulnerable.'
    },
    imagePath: appLibsLibraryIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="appLibraries"]',
        radius: 58,
        yOffset: -39,
        xOffset: -16
      }
    ]
  },
  {
    title: {
      id: 'title_librariesSlidingTutorial_getAnalysis',
      defaultMessage: 'Get Library Analysis'
    },
    description: {
      id: 'detail_librariesSlidingTutorial_getAnalysis',
      defaultMessage:
        'Contrast analyzes library files to assess security risks. We’ll tell you if classes are being used and present you with scoring and related stats. <div>Read our <a>scoring guide</a></div>',
      values: {
        div: (...msg) => <div>{msg}</div>,
        a: (...msg) => (
          <a
            href={intl.formatMessage({ id: 'link_libraryScoringGuide' })}
            rel="noopener noreferrer"
            target="_blank"
          >
            {msg}
          </a>
        )
      }
    },
    imagePath: appLibsStatsIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="libGrade"]',
        radius: 44,
        yOffset: -30,
        xOffset: -28
      }
    ]
  },
  {
    title: {
      id: 'title_librariesSlidingTutorial_drillIntoDetails',
      defaultMessage: 'Drill In To Library Details'
    },
    description: {
      id: 'detail_librariesSlidingTutorial_drillInToDetails',
      defaultMessage:
        'Drill into any library to find where it’s being used in your portfolio and get additional information on any known CVEs.'
    },
    imagePath: appLibsCVEIcon,
    circleIndicators: [
      {
        selector: '[tutorial-highlight="libTitle"]',
        radius: 66,
        yOffset: -68,
        xOffset: -24
      }
    ]
  }
]

const headerConfig: HeaderProps = {
  title: {
    id: 'title_librariesSlidingTutorial_inventoryUsedLibraries',
    defaultMessage: 'Inventory Used Libraries'
  },
  subtitle: {
    id: 'detail_librariesSlidingTutorial_inventoryUsedLibraries',
    defaultMessage: 'They directly impact the security of your app.'
  }
}

const panelConfig = (intl): PanelProps => ({
  renderHeader: () => {
    return <Header {...headerConfig} />
  },
  renderBody: () =>
    itemsConfig(intl).map((item, index) => <Item key={index} {...item} />),
  renderFooter: (renderFooterProps) => {
    return (
      <Button {...renderFooterProps}>
        <FormattedMessage id="action_gotIt_emphasis" defaultMessage="Got it!" />
      </Button>
    )
  }
})

export function LibrariesSlidingTutorial(props: CustomSlidingTutorialProps) {
  const intl = useIntl()

  return props.isVisible ? (
    <SlidingTutorialContent
      isOpen={props.isOpen}
      renderPanel={(renderPanelProps) => {
        return <Panel {...renderPanelProps} {...panelConfig(intl)} />
      }}
      tutorialKey={props.tutorialKey}
      orgUuid={props.orgUuid}
      yOffset={props.yOffset}
      zIndex={props.zIndex}
    />
  ) : null
}
